import React from 'react';
import { useAppContext } from '@/src/hooks/useAppContext';
import LoadingSpinner from './LoadingSpinner';
import ErrorIcon from './icons/ErrorIcon';
import BanknotesIcon from './icons/BanknotesIcon';
import ArrowUpCircleIcon from './icons/ArrowUpCircleIcon';
import ArrowDownCircleIcon from './icons/ArrowDownCircleIcon';
import { useTranslation } from 'react-i18next';

const TransactionHistoryView: React.FC = () => {
  const { accountTransactionsData: transactions, error, isLoading } = useAppContext();
  const { t } = useTranslation();

  const formatAmount = (amount: number) => {
    return `${amount} ${t('transaction_history.credits_unit')}`;
  };

  const formatAmountBilled = (amount: number) => {
    return `${amount}€`;
  };
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[200px]">
          <LoadingSpinner />
          <p className="mt-4 text-slate-600">{t('transaction_history.loading_message')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center text-center p-6 min-h-[200px]">
          <ErrorIcon className="w-12 h-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-red-600">{t('transaction_history.error_title')}</h3>
          <p className="text-slate-700">{error}</p>
        </div>
      );
    }

    if (!transactions || transactions.length === 0) {
      return (
        <div className="text-center py-16 px-6 bg-slate-50 rounded-lg">
          <BanknotesIcon className="w-12 h-12 mx-auto text-slate-400" />
          <h3 className="mt-4 text-lg font-semibold text-slate-700">{t('transaction_history.no_transactions_title')}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {t('transaction_history.no_transactions_message')}
          </p>
        </div>
      );
    }

    return (
      <>
        {/* Mobile View: List of Cards */}
        <div className="md:hidden space-y-3">
          {transactions.map((tx) => {
            const isDeposit = tx.type === 'DEPOSIT';
            return (
              <div key={tx.id} className="bg-white p-4 rounded-lg shadow-sm border border-slate-200/80 flex items-center justify-between gap-4">
                <div className="flex items-center min-w-0">
                  <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full ${isDeposit ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                    {isDeposit ? <ArrowUpCircleIcon className="w-6 h-6" /> : <ArrowDownCircleIcon className="w-6 h-6" />}
                  </div>
                  <div className="ml-3 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{tx.description || (isDeposit ? t('transaction_history.deposit_description') : t('transaction_history.solution_use_description'))}</p>
                    <p className="text-xs text-slate-500">{new Date(tx.timestamp * 1000).toLocaleDateString('sr-RS')}</p>
                  </div>
                </div>
                <div className={`font-bold flex-shrink-0 ${isDeposit ? 'text-green-600' : 'text-red-600'}`}>
                  {isDeposit ? '+' : '-'}{formatAmount(Math.abs(tx.amount_cents))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop View: Table */}
        <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full bg-white">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 uppercase font-semibold text-sm text-slate-600">{t('transaction_history.table_header_date')}</th>
                <th className="text-left py-3 px-4 uppercase font-semibold text-sm text-slate-600">{t('transaction_history.table_header_description')}</th>
                <th className="text-right py-3 px-4 uppercase font-semibold text-sm text-slate-600">{t('transaction_history.table_header_credits')}</th>
                <th className="text-right py-3 px-4 uppercase font-semibold text-sm text-slate-600">{t('transaction_history.table_header_amount')}</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {transactions.map((tx) => (
                <tr key={tx.id} className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="py-3 px-4">{new Date(tx.timestamp * 1000).toLocaleString('sr-RS')}</td>
                  <td className="py-3 px-4">{tx.description || (tx.type === 'DEPOSIT' ? t('transaction_history.deposit_description') : t('transaction_history.solution_use_description'))}</td>
                  <td className={`py-3 px-4 text-right font-medium ${tx.type === 'DEPOSIT' ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.type === 'DEPOSIT' ? '+' : '-'}{formatAmount(Math.abs(tx.amount_cents))}
                  </td>
                  <td className={`py-3 px-4 text-right font-medium ${tx.type === 'DEPOSIT' ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.type === 'DEPOSIT' && tx.amount_cents_billed > 0 ? ' ' + formatAmountBilled(Math.abs(tx.amount_cents_billed)) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <div className="bg-transparent md:bg-white p-0 md:p-6 lg:p-8 md:shadow-xl md:rounded-lg">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-sky-700 mb-8">{t('transaction_history.page_title')}</h2>
      {renderContent()}
    </div>
  );
};

export default TransactionHistoryView;